import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Box,
  Alert
} from '@mui/material';

const AddPatientDialog = ({ open, onClose, onPatientAdded }) => {
  const [formData, setFormData] = useState({
    mrn: '',
    name: '',
    dob: '',
    sex: '',
    phone: '',
    email: '',
    address: ''
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const API = process.env.REACT_APP_API_BASE;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.mrn || !formData.mrn.trim()) {
      newErrors.mrn = 'MRN is required';
    }
    
    if (!formData.name || !formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.dob) {
      newErrors.dob = 'Date of birth is required';
    }
    
    if (!formData.sex) {
      newErrors.sex = 'Sex is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    setSaving(true);
    setError('');

    try {
      const response = await fetch(`${API}/patients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const newPatient = await response.json();
        onPatientAdded(newPatient);
        handleClose();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create patient');
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({
      mrn: '',
      name: '',
      dob: '',
      sex: '',
      phone: '',
      email: '',
      address: ''
    });
    setErrors({});
    setError('');
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      data-testid="add-patient-dialog"
    >
      <DialogTitle>Add New Patient</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Medical Record Number (MRN)"
                value={formData.mrn}
                onChange={(e) => handleChange('mrn', e.target.value)}
                error={Boolean(errors.mrn)}
                helperText={errors.mrn}
                data-field-name="mrn"
                data-field-type="text"
                data-testid="patient-mrn-input"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Full Name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                error={Boolean(errors.name)}
                helperText={errors.name}
                data-field-name="name"
                data-field-type="text"
                data-testid="patient-name-input"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Date of Birth"
                type="date"
                value={formData.dob}
                onChange={(e) => handleChange('dob', e.target.value)}
                error={Boolean(errors.dob)}
                helperText={errors.dob}
                InputLabelProps={{
                  shrink: true,
                }}
                data-field-name="dob"
                data-field-type="date"
                data-testid="patient-dob-input"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                select
                label="Sex"
                value={formData.sex}
                onChange={(e) => handleChange('sex', e.target.value)}
                error={Boolean(errors.sex)}
                helperText={errors.sex}
                data-field-name="sex"
                data-field-type="select"
                data-testid="patient-sex-input"
              >
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="(555) 555-5555"
                data-field-name="phone"
                data-field-type="text"
                data-testid="patient-phone-input"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="patient@example.com"
                data-field-name="email"
                data-field-type="email"
                data-testid="patient-email-input"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="123 Main St, City, State 12345"
                data-field-name="address"
                data-field-type="text"
                data-testid="patient-address-input"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleClose}
          disabled={saving}
          data-action="cancel-add-patient"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          data-action="save-patient"
          data-testid="save-patient-button"
        >
          {saving ? 'Saving...' : 'Add Patient'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPatientDialog;
